import React, { useState, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, getDocs, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Icons } from '../components/Icons';
import { sendSlackNotification } from '../utils/slackNotification';

function AddInventory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  // 속성 관리
  const [attributes, setAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});

  // 카테고리 관리
  const [categoriesLarge, setCategoriesLarge] = useState([]);
  const [categoriesMedium, setCategoriesMedium] = useState([]);
  const [filteredCategoriesMedium, setFilteredCategoriesMedium] = useState([]);

  // 상태 및 컨디션 관리
  const [statusesLarge, setStatusesLarge] = useState([]);
  const [statusesMedium, setStatusesMedium] = useState([]);
  const [filteredStatusesMedium, setFilteredStatusesMedium] = useState([]);
  const [conditions, setConditions] = useState([]);

  // 위치 관리
  const [locations, setLocations] = useState([]);

  // 자동완성 데이터
  const [designers, setDesigners] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [materials, setMaterials] = useState([]);

  // 자동완성 표시 여부
  const [showDesignerSuggestions, setShowDesignerSuggestions] = useState(false);
  const [showManufacturerSuggestions, setShowManufacturerSuggestions] = useState(false);
  const [showMaterialSuggestions, setShowMaterialSuggestions] = useState(false);

  // 폼 데이터
  const [formData, setFormData] = useState({
    productName: '',
    customBarcodeId: '', // 직접 입력 SKU
    categoryLarge: '',
    categoryMedium: '',
    designer: '',
    manufacturer: '', // 제조사
    material: '', // 수종/색상
    era: '', // 연대
    sizeW: '', // 가로
    sizeD: '', // 세로
    sizeH: '', // 높이
    sizeSH: '', // 좌면높이
    ownershipType: '직판', // 직판 or 위탁
    condition: '',
    location: '',
    purchasePrice: '',
    salePrice: '',
    consignorName: '',
    consignorContact: '',
    consignorCommission: '',
    settlementPrice: '', // 정산가
    statusLarge: '',
    statusMedium: '',
    notes: '', // 특이사항
    purchaseDate: '', // 구매일
    arrivalDate: '', // 입고일 (날짜)
    arrivalBatch: '', // 입고일/컨테이너 (텍스트, 예: "25.6th(10월)")
    restorationCompleted: false, // 복원완료
    productType: 'unique', // 'unique' (일품) or 'bulk' (수량관리)
    quantity: 1 // 기본 수량 (수량관리 타입일 때만 사용)
  });

  // 위치별 수량 관리
  const [locationQuantities] = useState({});

  // 이미지 파일들
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);

  // 이미지 모달
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 연속 등록 모달
  const [continueModal, setContinueModal] = useState({ show: false, lastFormData: null });

  // 속성, 카테고리, 상태, 컨디션, 위치 불러오기
  useEffect(() => {
    fetchAttributes();
    fetchCategories();
    fetchStatusesAndConditions();
    fetchLocations();
    fetchAutoCompleteData();
  }, []);

  // 카테고리(대) 변경 시 카테고리(중) 필터링
  useEffect(() => {
    if (formData.categoryLarge) {
      const filtered = categoriesMedium.filter(
        cat => cat.parentCategory === formData.categoryLarge
      );
      setFilteredCategoriesMedium(filtered);
    } else {
      setFilteredCategoriesMedium([]);
    }
  }, [formData.categoryLarge, categoriesMedium]);

  // 상태(대) 변경 시 상태(중) 필터링
  useEffect(() => {
    if (formData.statusLarge) {
      const filtered = statusesMedium.filter(
        stat => stat.parentStatus === formData.statusLarge
      );
      setFilteredStatusesMedium(filtered);
    } else {
      setFilteredStatusesMedium([]);
    }
  }, [formData.statusLarge, statusesMedium]);

  const fetchAttributes = async () => {
    try {
      const attributesRef = collection(db, 'attributes');
      const snapshot = await getDocs(attributesRef);
      const attributeList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAttributes(attributeList);
    } catch (error) {
      console.error('속성 불러오기 오류:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const categoriesLargeRef = collection(db, 'filter_categories_large');
      const largeSnapshot = await getDocs(categoriesLargeRef);
      setCategoriesLarge(largeSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const categoriesMediumRef = collection(db, 'filter_categories_medium');
      const mediumSnapshot = await getDocs(categoriesMediumRef);
      setCategoriesMedium(mediumSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('카테고리 불러오기 오류:', error);
    }
  };

  const fetchStatusesAndConditions = async () => {
    try {
      const statusesLargeRef = collection(db, 'filter_statuses_large');
      const largeSnapshot = await getDocs(statusesLargeRef);
      setStatusesLarge(largeSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const statusesMediumRef = collection(db, 'filter_statuses_medium');
      const mediumSnapshot = await getDocs(statusesMediumRef);
      setStatusesMedium(mediumSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const conditionsRef = collection(db, 'filter_conditions');
      const conditionsSnapshot = await getDocs(conditionsRef);
      setConditions(conditionsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('상태 및 컨디션 불러오기 오류:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const locationsRef = collection(db, 'locations');
      const snapshot = await getDocs(locationsRef);
      const locationList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // name 필드로 정렬
      locationList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setLocations(locationList);
    } catch (error) {
      console.error('위치 불러오기 오류:', error);
    }
  };

  const fetchAutoCompleteData = async () => {
    try {
      const inventoryRef = collection(db, 'inventory');
      const snapshot = await getDocs(inventoryRef);

      const designerSet = new Set();
      const manufacturerSet = new Set();
      const materialSet = new Set();

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.designer && data.designer.trim()) {
          designerSet.add(data.designer.trim());
        }
        if (data.manufacturer && data.manufacturer.trim()) {
          manufacturerSet.add(data.manufacturer.trim());
        }
        if (data.material && data.material.trim()) {
          materialSet.add(data.material.trim());
        }
      });

      setDesigners(Array.from(designerSet).sort());
      setManufacturers(Array.from(manufacturerSet).sort());
      setMaterials(Array.from(materialSet).sort());
    } catch (error) {
      console.error('자동완성 데이터 불러오기 오류:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const newValue = type === 'checkbox' ? checked : value;
      // 일품 선택 시 수량을 1로 리셋
      if (name === 'productType' && value === 'unique') {
        return { ...prev, [name]: newValue, quantity: 1 };
      }
      return { ...prev, [name]: newValue };
    });
  };

  // 사이즈 입력 시 컴마 자동 추가
  const handleSizeChange = (e) => {
    const { name, value } = e.target;
    // 숫자와 컴마만 허용
    const numericValue = value.replace(/[^\d]/g, '');
    // 1000단위 컴마 추가
    const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    setImageFiles(prevFiles => [...prevFiles, ...files]);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const openImageModal = (preview) => {
    setSelectedImage(preview);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  const uploadImages = async (productId) => {
    const uploadPromises = imageFiles.map(async (file, index) => {
      const storageRef = ref(storage, `inventory/${productId}/${index}_${file.name}`);
      await uploadBytes(storageRef, file);
      return getDownloadURL(storageRef);
    });

    return Promise.all(uploadPromises);
  };

  const generateBarcodeId = (categoryLarge) => {
    // 카테고리별 접두사 매핑
    const categoryPrefixMap = {
      'TABLE': 'TAD',
      'STORAGE': 'STD',
      'ETC': 'ETD',
      'VARIOUS': 'VAD',
      'SEATING': 'CHD',
      'LIGHTING': 'LTD'
    };

    const prefix = categoryPrefixMap[categoryLarge?.toUpperCase()] || 'OOM';
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 7);
    return `${prefix}${timestamp}${random}`.toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let docRef = null; // 롤백을 위한 문서 참조 저장

    try {
      // 직접 입력한 SKU가 있으면 사용, 없으면 카테고리 기반 자동 생성
      const barcodeId = formData.customBarcodeId.trim() || generateBarcodeId(formData.categoryLarge);

      // Size 조합 (컬마 제거 후 조합)
      const cleanSizeW = formData.sizeW.replace(/,/g, '') || '***';
      const cleanSizeD = formData.sizeD.replace(/,/g, '') || '***';
      const cleanSizeH = formData.sizeH.replace(/,/g, '') || '***';
      const cleanSizeSH = formData.sizeSH.replace(/,/g, '') || '***';
      const sizeString = `${cleanSizeW}mm (W) x ${cleanSizeD}mm (D) x ${cleanSizeH}mm (H) x ${cleanSizeSH}mm (SH)`;

      // Firestore에 재고 추가
      docRef = await addDoc(collection(db, 'inventory'), {
        ...formData,
        size: sizeString,
        category: `${formData.categoryLarge} > ${formData.categoryMedium}`,
        status: formData.statusMedium ? `${formData.statusLarge} > ${formData.statusMedium}` : formData.statusLarge,
        purchasePrice: parseInt(formData.purchasePrice) || 0,
        salePrice: parseInt(formData.salePrice) || 0,
        consignorCommission: parseFloat(formData.consignorCommission) || 0,
        quantity: parseInt(formData.quantity) || 1,
        locationQuantities: locationQuantities, // 위치별 수량
        barcodeId,
        photos: [],
        attributes: attributeValues, // 속성 값 추가
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: user.email
      });

      // 이미지 업로드
      if (imageFiles.length > 0) {
        try {
          const photoUrls = await uploadImages(docRef.id);

          // Firestore 업데이트
          await updateDoc(doc(db, 'inventory', docRef.id), {
            photos: photoUrls
          });
        } catch (uploadError) {
          console.error('이미지 업로드 오류:', uploadError);
          // 이미지 업로드 실패 시 생성된 재고 문서 삭제 (롤백)
          await deleteDoc(doc(db, 'inventory', docRef.id));
          throw new Error('이미지 업로드에 실패했습니다. 재고 등록이 취소되었습니다.');
        }
      }

      // 이력 기록
      await addDoc(collection(db, 'inventory_history'), {
        productId: docRef.id,
        changeType: '등록',
        newValue: formData,
        timestamp: serverTimestamp(),
        userId: user.email
      });

      // Slack 알림 발송
      await sendSlackNotification('inventoryAdd', {
        productName: formData.productName,
        barcodeId: barcodeId,
        category: `${formData.categoryLarge} > ${formData.categoryMedium}`,
        location: formData.location,
        purchasePrice: parseInt(formData.purchasePrice) || 0,
        salePrice: parseInt(formData.salePrice) || 0
      }, user.email);

      // 연속 등록 모달 표시
      setContinueModal({ show: true, lastFormData: { ...formData } });
    } catch (error) {
      console.error('재고 등록 오류:', error);
      alert(error.message || '재고 등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // 연속 등록 - 리스트로 돌아가기
  const handleGoToList = () => {
    setContinueModal({ show: false, lastFormData: null });
    navigate('/inventory');
  };

  // 연속 등록 - 같은 정보로 계속 등록
  const handleContinueWithSameData = () => {
    const lastData = continueModal.lastFormData;
    setFormData({
      ...lastData,
      productName: '', // 제품명은 비움
      customBarcodeId: '', // SKU도 비움
      notes: '', // 특이사항도 비움
    });
    setImageFiles([]);
    setImagePreviews([]);
    setCurrentStep(1);
    setContinueModal({ show: false, lastFormData: null });
  };

  // 연속 등록 - 새로 등록
  const handleContinueWithNewData = () => {
    setFormData({
      productName: '',
      customBarcodeId: '',
      categoryLarge: '',
      categoryMedium: '',
      designer: '',
      manufacturer: '',
      material: '',
      era: '',
      sizeW: '',
      sizeD: '',
      sizeH: '',
      sizeSH: '',
      ownershipType: '직판',
      condition: '',
      location: '',
      purchasePrice: '',
      salePrice: '',
      consignorName: '',
      consignorContact: '',
      consignorCommission: '',
      settlementPrice: '',
      statusLarge: '',
      statusMedium: '',
      notes: '',
      purchaseDate: '',
      arrivalDate: '',
      arrivalBatch: '',
      restorationCompleted: false,
      productType: 'unique',
      quantity: 1
    });
    setAttributeValues({});
    setImageFiles([]);
    setImagePreviews([]);
    setCurrentStep(1);
    setContinueModal({ show: false, lastFormData: null });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold text-brand-900">재고 등록</h2>
        <div className="text-sm text-gray-500">
          새로운 상품을 시스템에 등록합니다.
        </div>
      </div>

      {/* 진행 단계 표시 */}
      <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-brand-100">
        <div className="flex items-center justify-between relative">
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-100 -z-10" />

          {['기본 정보', '가격 정보', '확인'].map((step, index) => {
            const stepNum = index + 1;
            const isActive = currentStep >= stepNum;
            const isCurrent = currentStep === stepNum;

            return (
              <div key={step} className="flex flex-col items-center bg-white px-2">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${isActive
                      ? 'bg-brand-600 text-white shadow-md scale-110'
                      : 'bg-gray-100 text-gray-400 border border-gray-200'
                    }`}
                >
                  {isActive ? (isCurrent ? stepNum : <Icons.Check className="w-5 h-5" />) : stepNum}
                </div>
                <span className={`mt-2 text-xs font-medium transition-colors duration-300 ${isActive ? 'text-brand-800' : 'text-gray-400'
                  }`}>
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-brand-100 p-8 animate-fade-in-up">
        {/* Step 1: 기본 정보 */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-brand-900 border-b border-gray-100 pb-3">기본 정보</h3>

            <div>
              <label className="block text-sm font-medium text-brand-700 mb-2">
                제품명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                placeholder="제품명을 입력하세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                required
              />
            </div>

            {/* SKU/바코드 ID 직접 입력 */}
            <div>
              <label className="block text-sm font-medium text-brand-700 mb-2">
                SKU (제품코드)
              </label>
              <input
                type="text"
                name="customBarcodeId"
                value={formData.customBarcodeId}
                onChange={handleChange}
                placeholder="미입력시 자동 생성 (예: TAD로 시작)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                비워두면 카테고리별 자동 생성: TABLE→TAD, STORAGE→STD, SEATING→CHD, LIGHTING→LTD, ETC→ETD, VARIOUS→VAD
              </p>
            </div>

            {/* 제품 유형 선택 */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <label className="block text-sm font-medium text-brand-700 mb-3">
                제품 유형 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className={`flex-1 flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.productType === 'unique'
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="productType"
                    value="unique"
                    checked={formData.productType === 'unique'}
                    onChange={handleChange}
                    className="w-4 h-4 text-brand-600"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-gray-900">일품</span>
                    <p className="text-xs text-gray-500 mt-0.5">빈티지/고유 제품 (개별 관리)</p>
                  </div>
                </label>
                <label className={`flex-1 flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.productType === 'bulk'
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="productType"
                    value="bulk"
                    checked={formData.productType === 'bulk'}
                    onChange={handleChange}
                    className="w-4 h-4 text-brand-600"
                  />
                  <div className="ml-3">
                    <span className="font-medium text-gray-900">수량관리</span>
                    <p className="text-xs text-gray-500 mt-0.5">동일 제품 여러 개 (수량 증감)</p>
                  </div>
                </label>
              </div>

              {/* 수량 입력 (수량관리 타입일 때만 표시) */}
              {formData.productType === 'bulk' && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-brand-700 mb-2">
                    초기 수량 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                      className="w-10 h-10 rounded-lg bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-xl font-bold text-gray-600 transition"
                    >
                      -
                    </button>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={(e) => setFormData(prev => ({ ...prev, quantity: Math.max(1, parseInt(e.target.value) || 1) }))}
                      min="1"
                      className="w-24 px-4 py-2 border border-gray-300 rounded-lg text-center text-lg font-bold focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                      className="w-10 h-10 rounded-lg bg-brand-600 hover:bg-brand-700 flex items-center justify-center text-xl font-bold text-white transition"
                    >
                      +
                    </button>
                    <span className="text-gray-500 ml-2">개</span>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  카테고리(대) <span className="text-red-500">*</span>
                </label>
                <select
                  name="categoryLarge"
                  value={formData.categoryLarge}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                  required
                >
                  <option value="">카테고리(대) 선택</option>
                  {categoriesLarge.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  카테고리(중)
                </label>
                <select
                  name="categoryMedium"
                  value={formData.categoryMedium}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                  disabled={!formData.categoryLarge}
                >
                  <option value="">
                    {formData.categoryLarge ? '카테고리(중) 선택' : '먼저 카테고리(대)를 선택하세요'}
                  </option>
                  {filteredCategoriesMedium.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Designer 자동완성 */}
              <div className="relative">
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  Designer
                </label>
                <input
                  type="text"
                  name="designer"
                  value={formData.designer}
                  onChange={handleChange}
                  onFocus={() => setShowDesignerSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowDesignerSuggestions(false), 200)}
                  placeholder="Designer name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                />
                {showDesignerSuggestions && designers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {designers
                      .filter(d => d.toLowerCase().includes(formData.designer.toLowerCase()))
                      .slice(0, 10)
                      .map((designer, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, designer }));
                            setShowDesignerSuggestions(false);
                          }}
                          className="px-4 py-2 hover:bg-brand-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <span className="text-gray-800">{designer}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Manufacturer 자동완성 */}
              <div className="relative">
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  Manufacturer
                </label>
                <input
                  type="text"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleChange}
                  onFocus={() => setShowManufacturerSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowManufacturerSuggestions(false), 200)}
                  placeholder="Manufacturer name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                />
                {showManufacturerSuggestions && manufacturers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {manufacturers
                      .filter(m => m.toLowerCase().includes(formData.manufacturer.toLowerCase()))
                      .slice(0, 10)
                      .map((manufacturer, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, manufacturer }));
                            setShowManufacturerSuggestions(false);
                          }}
                          className="px-4 py-2 hover:bg-brand-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <span className="text-gray-800">{manufacturer}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Material 자동완성 */}
              <div className="relative">
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  Material
                </label>
                <input
                  type="text"
                  name="material"
                  value={formData.material}
                  onChange={handleChange}
                  onFocus={() => setShowMaterialSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowMaterialSuggestions(false), 200)}
                  placeholder="e.g., Steel, Wood, Leather, Fabric"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                />
                {showMaterialSuggestions && materials.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {materials
                      .filter(m => m.toLowerCase().includes(formData.material.toLowerCase()))
                      .slice(0, 10)
                      .map((material, index) => (
                        <div
                          key={index}
                          onClick={() => {
                            setFormData(prev => ({ ...prev, material }));
                            setShowMaterialSuggestions(false);
                          }}
                          className="px-4 py-2 hover:bg-brand-50 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <span className="text-gray-800">{material}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Era 입력 필드 */}
              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  Era (연대)
                </label>
                <input
                  type="text"
                  name="era"
                  value={formData.era}
                  onChange={handleChange}
                  placeholder="e.g., 1960s, 1950-60s, 1970"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                />
              </div>
            </div>

            {/* Size 입력 필드 */}
            <div>
              <label className="block text-sm font-medium text-brand-700 mb-2">
                Size
              </label>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <input
                    type="text"
                    name="sizeW"
                    value={formData.sizeW}
                    onChange={handleSizeChange}
                    placeholder="1,200"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors text-center"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">(W) mm</p>
                </div>
                <div>
                  <input
                    type="text"
                    name="sizeD"
                    value={formData.sizeD}
                    onChange={handleSizeChange}
                    placeholder="800"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors text-center"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">(D) mm</p>
                </div>
                <div>
                  <input
                    type="text"
                    name="sizeH"
                    value={formData.sizeH}
                    onChange={handleSizeChange}
                    placeholder="750"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors text-center"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">(H) mm</p>
                </div>
                <div>
                  <input
                    type="text"
                    name="sizeSH"
                    value={formData.sizeSH}
                    onChange={handleSizeChange}
                    placeholder="450"
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors text-center"
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">(SH) mm</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                형식: {formData.sizeW || '1200'}mm (W) x {formData.sizeD || '800'}mm (D) x {formData.sizeH || '750'}mm (H) x {formData.sizeSH || '450'}mm (SH)
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  컨디션 <span className="text-red-500">*</span>
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                  required
                >
                  <option value="">컨디션 선택</option>
                  {conditions.map((cond) => (
                    <option key={cond.id} value={cond.name}>
                      {cond.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  입고일(날짜) <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="arrivalDate"
                  value={formData.arrivalDate}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  구매일
                </label>
                <input
                  type="date"
                  name="purchaseDate"
                  value={formData.purchaseDate}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  입고일(컨테이너)
                </label>
                <input
                  type="text"
                  name="arrivalBatch"
                  value={formData.arrivalBatch}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                  placeholder="예: 25.6th(10월)"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  상태(대) <span className="text-red-500">*</span>
                </label>
                <select
                  name="statusLarge"
                  value={formData.statusLarge}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                  required
                >
                  <option value="">상태(대) 선택</option>
                  {statusesLarge.map((status) => (
                    <option key={status.id} value={status.name}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  상태(중)
                </label>
                <select
                  name="statusMedium"
                  value={formData.statusMedium}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                  disabled={!formData.statusLarge}
                >
                  <option value="">
                    {formData.statusLarge ? '상태(중) 선택' : '먼저 상태(대)를 선택하세요'}
                  </option>
                  {filteredStatusesMedium.map((status) => (
                    <option key={status.id} value={status.name}>
                      {status.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-700 mb-2">
                위치 <span className="text-red-500">*</span>
              </label>
              <select
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors bg-white"
                required
              >
                <option value="">위치 선택</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.name}>{loc.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-brand-700 mb-2">
                비고
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="특이사항이나 메모를 입력하세요"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
              />
            </div>

            {/* 속성 필드 */}
            {attributes.length > 0 && (
              <div className="pt-6 border-t border-gray-200">
                <h4 className="text-lg font-bold text-brand-900 mb-4 flex items-center">
                  <Icons.Tag className="w-5 h-5 mr-2 text-brand-600" />
                  제품 속성
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {attributes.map((attribute) => (
                    <div key={attribute.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {attribute.name}
                      </label>
                      {attribute.type === 'select' ? (
                        <select
                          value={attributeValues[attribute.id] || ''}
                          onChange={(e) => setAttributeValues({
                            ...attributeValues,
                            [attribute.id]: e.target.value
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
                        >
                          <option value="">선택하세요</option>
                          {attribute.options?.map((option, idx) => (
                            <option key={idx} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : attribute.type === 'number' ? (
                        <input
                          type="number"
                          value={attributeValues[attribute.id] || ''}
                          onChange={(e) => setAttributeValues({
                            ...attributeValues,
                            [attribute.id]: e.target.value
                          })}
                          placeholder={`${attribute.name} 입력`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      ) : attribute.type === 'date' ? (
                        <input
                          type="date"
                          value={attributeValues[attribute.id] || ''}
                          onChange={(e) => setAttributeValues({
                            ...attributeValues,
                            [attribute.id]: e.target.value
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      ) : (
                        <input
                          type="text"
                          value={attributeValues[attribute.id] || ''}
                          onChange={(e) => setAttributeValues({
                            ...attributeValues,
                            [attribute.id]: e.target.value
                          })}
                          placeholder={`${attribute.name} 입력`}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 사진 등록 (선택사항) */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h4 className="text-lg font-semibold text-brand-900 mb-4">사진 등록 (선택사항)</h4>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-brand-500 transition-colors bg-gray-50">
                <input
                  type="file"
                  id="file-upload"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center justify-center">
                  <div className="w-12 h-12 bg-brand-100 text-brand-600 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-900">사진을 선택하거나 드래그하세요</span>
                  <p className="text-xs text-gray-500 mt-1">
                    여러 장의 사진을 동시에 선택할 수 있습니다.
                  </p>
                </label>
              </div>

              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group rounded-lg overflow-hidden shadow-sm border border-gray-200">
                      <img
                        src={preview}
                        alt={`미리보기 ${index + 1}`}
                        className="w-full h-24 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openImageModal(preview)}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() => openImageModal(preview)}
                          className="bg-blue-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-200 hover:bg-blue-600 shadow-lg"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-200 hover:bg-red-600 shadow-lg"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: 가격 및 소유 정보 */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-brand-900 border-b border-gray-100 pb-3">가격 및 소유 정보</h3>

            {/* 소유 구분 */}
            <div>
              <label className="block text-sm font-medium text-brand-700 mb-2">
                소유 구분 <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="ownershipType"
                    value="직판"
                    checked={formData.ownershipType === '직판'}
                    onChange={handleChange}
                    className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">직판</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="ownershipType"
                    value="위탁"
                    checked={formData.ownershipType === '위탁'}
                    onChange={handleChange}
                    className="w-4 h-4 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">위탁</span>
                </label>
              </div>
            </div>

            {/* 복원완료 체크박스 */}
            <div>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="restorationCompleted"
                  checked={formData.restorationCompleted}
                  onChange={handleChange}
                  className="w-5 h-5 text-brand-600 rounded focus:ring-brand-500"
                />
                <span className="ml-3 text-sm font-medium text-brand-700">복원완료</span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  구매가
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">₩</span>
                  <input
                    type="number"
                    name="purchasePrice"
                    value={formData.purchasePrice}
                    onChange={handleChange}
                    placeholder="구매가 입력"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-700 mb-2">
                  판매가
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">₩</span>
                  <input
                    type="number"
                    name="salePrice"
                    value={formData.salePrice}
                    onChange={handleChange}
                    placeholder="판매가 입력"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    required
                  />
                </div>
              </div>
            </div>

            {formData.ownershipType === '위탁' && (
              <div className="bg-brand-50 p-6 rounded-xl border border-brand-100 mt-6">
                <h4 className="text-lg font-semibold text-brand-900 mb-4 flex items-center">
                  <Icons.Users className="w-5 h-5 mr-2" />
                  위탁자 정보
                </h4>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brand-700 mb-2">
                      위탁자 이름
                    </label>
                    <input
                      type="text"
                      name="consignorName"
                      value={formData.consignorName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-700 mb-2">
                      위탁자 연락처
                    </label>
                    <input
                      type="tel"
                      name="consignorContact"
                      value={formData.consignorContact}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-700 mb-2">
                      수수료율 (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      name="consignorCommission"
                      value={formData.consignorCommission}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-brand-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brand-700 mb-2">
                      정산가 (원)
                    </label>
                    <input
                      type="text"
                      name="settlementPrice"
                      value={formData.settlementPrice ? Number(formData.settlementPrice).toLocaleString() : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                        setFormData(prev => ({ ...prev, settlementPrice: value }));
                      }}
                      placeholder="위탁자에게 정산할 금액"
                      className="w-full px-4 py-3 border border-brand-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: 확인 */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-brand-900 border-b border-gray-100 pb-3">등록 정보 확인</h3>

            <div className="bg-gray-50 rounded-xl p-6 space-y-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">기본 정보</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">제품명</span>
                      <span className="font-medium text-gray-900">{formData.productName}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">카테고리(대)</span>
                      <span className="font-medium text-gray-900">{formData.categoryLarge || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">카테고리(중)</span>
                      <span className="font-medium text-gray-900">{formData.categoryMedium || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">Designer</span>
                      <span className="font-medium text-gray-900">{formData.designer || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">Manufacturer</span>
                      <span className="font-medium text-gray-900">{formData.manufacturer || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">Material</span>
                      <span className="font-medium text-gray-900">{formData.material || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">Era (연대)</span>
                      <span className="font-medium text-gray-900">{formData.era || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">Size</span>
                      <span className="font-medium text-gray-900">
                        {formData.sizeW || formData.sizeD || formData.sizeH || formData.sizeSH
                          ? `${formData.sizeW || '***'} (W) x ${formData.sizeD || '***'} (D) x ${formData.sizeH || '***'} (H) x ${formData.sizeSH || '***'} (SH)`
                          : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">컨디션</span>
                      <span className="font-medium text-gray-900">{formData.condition}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">위치</span>
                      <span className="font-medium text-gray-900">{formData.location}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">입고일(날짜)</span>
                      <span className="font-medium text-gray-900">{formData.arrivalDate || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">구매일</span>
                      <span className="font-medium text-gray-900">{formData.purchaseDate || '-'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">입고일(컨테이너)</span>
                      <span className="font-medium text-gray-900">{formData.arrivalBatch || '-'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">가격 및 소유 정보</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">소유 구분</span>
                      <span className="font-medium text-gray-900">{formData.ownershipType}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">복원완료</span>
                      <span className="font-medium text-gray-900">{formData.restorationCompleted ? '예' : '아니오'}</span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">판매가</span>
                      <span className="font-bold text-brand-600 text-lg">
                        {parseInt(formData.salePrice).toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">구매가</span>
                      <span className="font-medium text-gray-900">
                        {formData.purchasePrice ? parseInt(formData.purchasePrice).toLocaleString() + '원' : '-'}
                      </span>
                    </div>
                    {formData.ownershipType === '위탁' && (
                      <>
                        <div className="flex justify-between border-b border-gray-200 pb-2">
                          <span className="text-gray-600">위탁자</span>
                          <span className="font-medium text-gray-900">{formData.consignorName || '-'}</span>
                        </div>
                        <div className="flex justify-between border-b border-gray-200 pb-2">
                          <span className="text-gray-600">위탁자 연락처</span>
                          <span className="font-medium text-gray-900">{formData.consignorContact || '-'}</span>
                        </div>
                        <div className="flex justify-between border-b border-gray-200 pb-2">
                          <span className="text-gray-600">수수료율</span>
                          <span className="font-medium text-gray-900">{formData.consignorCommission ? formData.consignorCommission + '%' : '-'}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between border-b border-gray-200 pb-2">
                      <span className="text-gray-600">사진 수</span>
                      <span className="font-medium text-gray-900">{imageFiles.length}장</span>
                    </div>
                  </div>
                </div>
              </div>

              {formData.notes && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">특이사항</h4>
                  <p className="text-gray-700 bg-white p-3 rounded-lg border border-gray-200 text-sm">
                    {formData.notes}
                  </p>
                </div>
              )}

              {imagePreviews.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">사진 등록</h4>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`사진 ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => openImageModal(preview)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 버튼 그룹 */}
        <div className="flex justify-between mt-10 pt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={prevStep}
            disabled={currentStep === 1}
            className={`px-6 py-2.5 rounded-lg transition font-medium ${currentStep === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
          >
            이전
          </button>

          {currentStep < 3 ? (
            <button
              type="button"
              onClick={nextStep}
              className="px-8 py-2.5 bg-brand-900 hover:bg-brand-800 text-white rounded-lg transition shadow-sm font-medium flex items-center"
            >
              다음
              <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition shadow-md font-bold flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  등록 중...
                </>
              ) : (
                <>
                  <Icons.Check className="w-5 h-5 mr-2" />
                  등록 완료
                </>
              )}
            </button>
          )}
        </div>
      </form>

      {/* 이미지 확대 모달 */}
      {isModalOpen && selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={closeImageModal}
        >
          <div className="relative max-w-6xl max-h-full">
            <button
              onClick={closeImageModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition"
            >
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedImage}
              alt="확대 이미지"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* 연속 등록 모달 */}
      {continueModal.show && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in-up">
            {/* 헤더 */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Icons.Check className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">등록 완료!</h3>
                  <p className="text-green-100 text-sm">재고가 성공적으로 등록되었습니다</p>
                </div>
              </div>
            </div>

            {/* 본문 */}
            <div className="p-6">
              <p className="text-gray-700 text-center mb-6">
                이어서 등록하시겠습니까?
              </p>

              {/* 선택 버튼들 */}
              <div className="space-y-3">
                <button
                  onClick={handleContinueWithSameData}
                  className="w-full flex items-center gap-3 px-4 py-4 bg-brand-50 hover:bg-brand-100 border border-brand-200 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                    <Icons.Copy className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-brand-700">방금 등록한 정보 사용</div>
                    <div className="text-xs text-brand-500">카테고리, 디자이너, 위치 등 유지</div>
                  </div>
                </button>

                <button
                  onClick={handleContinueWithNewData}
                  className="w-full flex items-center gap-3 px-4 py-4 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <Icons.Add className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-700">새로 등록하기</div>
                    <div className="text-xs text-gray-500">모든 정보 초기화하고 새로 시작</div>
                  </div>
                </button>
              </div>

              {/* 리스트로 돌아가기 */}
              <button
                onClick={handleGoToList}
                className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
              >
                재고 목록으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddInventory;
